import { ChangeDetectionStrategy, Component, HostListener, inject, Renderer2, signal, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NotificationsModule } from 'app/layout/common/notifications/notifications.module';
import {
  TsOverlayModule,
  TSIconModule,
  TsBadgeModule,
  TsInputComponent,
  TsDividerComponent,
  TsOverlayComponent,
} from 'tsui';
import { TsButtonModule } from 'tsui';
import { TsAvatarGroupComponent } from 'tsui/lib/ts-avatar-group/ts-avatar-group.component';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ShellSearchModalComponent } from '../shell-search-modal/shell-search-modal.component';
import { Router, RouterModule } from '@angular/router';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { AsyncPipe, DOCUMENT, NgClass } from '@angular/common';
import { environment } from 'environments/environment';
import { UserState } from '@state/user/state';
import { CreateWorkspaceComponent } from 'app/layout/common/create-workspace/create-workspace.component';
import { Dialog } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { SetUserStyleSettings } from '@state/user/actions';
import { LucideAngularModule } from 'lucide-angular';
import { CelesteToggleComponent } from 'app/layout/common/celeste-toggle/celeste-toggle.component';

@Component({
  selector: 'shell-action-bar',
  standalone: true,
  imports: [
    TsOverlayModule,
    TsButtonModule,
    TSIconModule,
    TsAvatarGroupComponent,
    NotificationsModule,
    TsBadgeModule,
    MatDialogModule,
    TsInputComponent,
    TsDividerComponent,
    AsyncPipe,
    RouterModule,
    NgClass,
    LucideAngularModule,
    CelesteToggleComponent,
  ],
  templateUrl: './shell-action-bar.component.html',
  styleUrl: './shell-action-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellActionBarComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly renderer2 = inject(Renderer2);

  workspaceSelectorOverlayBox = viewChild<TsOverlayComponent>('workspaceSelectorOverlayBox');
  searchControl = new FormControl('');
  private readonly isMacOS = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  searchPlaceholder = `Search (${this.isMacOS ? '⌘' : 'Ctrl +'} K)`;
  environment = environment.dataDog.env;
  styleOptionsOpen = signal(false);
  styleSettings$ = this.store.select(UserState.getUserStyleSettings);
  account$ = this.store.select(AccountState.getAccount);
  isTermSheetUser$ = this.store.select(UserState.isTermSheetUser);
  user$ = this.store.select(UserState.getUser);

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const isShortcutPressed = this.isMacOS
      ? event.metaKey && event.key.toLowerCase() === 'k'
      : event.ctrlKey && event.key.toLowerCase() === 'k';

    if (!isShortcutPressed) {
      return;
    }

    event.preventDefault();
    this.openSearchModal();
  }

  openSearchModal(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.matDialog.open(ShellSearchModalComponent, {
      width: '60vw',
      minHeight: '25vh',
      maxHeight: '80vh',
      minWidth: '60vw',
      position: {
        top: '50px',
      },
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
  }

  openSettings(): void {
    this.router.navigate(['/settings/general']);
    this.workspaceSelectorOverlayBox().closeOverlay();
  }

  openPeople(): void {
    this.router.navigate(['/settings/users']);
    this.workspaceSelectorOverlayBox().closeOverlay();
  }

  openCreateWorkspaceDialog(): void {
    this.dialog.open(CreateWorkspaceComponent, {
      ...TsDialogComponent.getDefaultDialogConfig(),
    });
  }

  setFontSize(size: 'compact' | 'small' | 'normal'): void {
    switch (size) {
      case 'compact':
        this.renderer2.removeClass(this.document.body, 'small');
        this.renderer2.addClass(this.document.body, 'compact');
        localStorage.setItem('style-settings', 'compact');
        break;
      case 'small':
        this.renderer2.removeClass(this.document.body, 'compact');
        this.renderer2.addClass(this.document.body, 'small');
        localStorage.setItem('style-settings', 'small');
        break;
      case 'normal':
        this.renderer2.removeClass(this.document.body, 'compact');
        this.renderer2.removeClass(this.document.body, 'small');
        localStorage.removeItem('style-settings');
        break;
    }

    this.store.dispatch(new SetUserStyleSettings(size));
  }

  toggleStyleOptions(): void {
    this.styleOptionsOpen.set(!this.styleOptionsOpen());
  }
}
